import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CreditCard, LogOut, Loader2, AlertTriangle, Sparkles, KeyRound, Trash2 } from 'lucide-react';
import { Meta } from '../seo/Meta.jsx';
import { logout, useSession } from '../lib/auth.js';
import PlanPicker from '../components/PlanPicker.jsx';
import ApiKeyModal from '../components/ApiKeyModal.jsx';

// Dashboard paywall placeholder per PRD-003.
//
// Logic:
//   loading            -> spinner
//   no workspace       -> AuthGuard redirects to /workspace-setup before this renders
//   status=pending_payment -> paywall card "Subscribe to activate"
//   status=active          -> Quickstart placeholder (filled in by PRD-009)
//   status=suspended/closed -> warning card
//
// Rich dashboard UI (live feed, watchlists, webhooks tabs) shipped in PRD-009.
export default function Dashboard() {
  const { workspaces, loading, error } = useSession();
  const [polled, setPolled] = useState(null);
  const ws = (polled && polled[0]) || (workspaces && workspaces[0]);

  // After Stripe Checkout returns (?checkout=return), poll /v1/workspace
  // every 2s for up to 30s until status flips to active. Webhook fires
  // checkout.session.completed and our handler updates the row; this loop
  // bridges the latency.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'return') return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled || attempts++ > 15) return;
      try {
        const r = await fetch('/v1/workspace', { credentials: 'include', headers: { Accept: 'application/json' } });
        if (r.ok) {
          const data = await r.json();
          setPolled(data.workspaces || []);
          if (data.workspaces && data.workspaces[0] && data.workspaces[0].status === 'active') {
            // Strip the query param so reloads do not re-poll.
            const u = new URL(window.location.href);
            u.searchParams.delete('checkout');
            u.searchParams.delete('session_id');
            window.history.replaceState({}, '', u.toString());
            return;
          }
        }
      } catch (_) { /* network blip  -  keep polling */ }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, []);

  const isActivating = !!new URLSearchParams(window.location.search).get('checkout');

  // Active workspaces redirect to Console (unless mid-checkout flow).
  if (!loading && !error && ws && ws.status === 'active' && !isActivating) {
    return <Navigate to="/console/overview" replace />;
  }

  return (
    <>
      <Meta title="Dashboard  -  tender-api.com" description="Your tender-api workspace dashboard." path="/dashboard" noindex={true} />
      <div style={page}>
        <Header workspace={ws} />
        <main style={main}>
          {loading && <SpinnerCard />}
          {!loading && error && <ErrorCard message="Could not load your workspace. Try refreshing." />}
          {!loading && !error && ws && ws.status === 'pending_payment' && isActivating && <ActivatingCard />}
          {!loading && !error && ws && ws.status === 'pending_payment' && !isActivating && <PaywallCard workspace={ws} />}
          {!loading && !error && ws && ws.status === 'active' && <QuickstartCard workspace={ws} />}
          {!loading && !error && ws && ws.status === 'past_due' && <PastDueCard workspace={ws} />}
          {!loading && !error && ws && ws.status === 'suspended' && <SuspendedCard workspace={ws} />}
          {!loading && !error && ws && (ws.status === 'closed' || ws.status === 'canceled') && <ClosedCard />}
        </main>
      </div>
    </>
  );
}

function ActivatingCard() {
  return (
    <Card>
      <CardIcon><Loader2 size={28} className="spin" color="var(--accent)" /></CardIcon>
      <h1 style={cardH1}>Activating your subscription…</h1>
      <p style={cardP}>Stripe is finalising the payment. This usually takes 5–10 seconds  -  sit tight, we will refresh automatically.</p>
    </Card>
  );
}

function PastDueCard({ workspace }) {
  return (
    <Card>
      <CardIcon><AlertTriangle size={28} strokeWidth={2} color="var(--danger)" /></CardIcon>
      <h1 style={cardH1}>Payment past due</h1>
      <p style={cardP}>
        <strong>{workspace.name}</strong> has an unpaid invoice. API access continues for 7 days then suspends. Update your payment method to keep your workspace active.
      </p>
      <a href="/pricing" style={primaryBtn}>Open billing portal</a>
    </Card>
  );
}

function Header({ workspace }) {
  return (
    <header style={hdr}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={brand}>tender-api.com</span>
        {workspace && (
          <span style={badge} title={`workspace status: ${workspace.status}`}>
            {workspace.name} · {workspace.status}
          </span>
        )}
      </div>
      <button onClick={() => logout()} style={logoutBtn} type="button">
        <LogOut size={14} strokeWidth={2} /> Sign out
      </button>
    </header>
  );
}

function PaywallCard({ workspace }) {
  return (
    <Card wide>
      <CardIcon>
        <CreditCard size={28} strokeWidth={2} color="var(--accent)" />
      </CardIcon>
      <h1 style={cardH1}>Subscribe to activate your workspace</h1>
      <p style={cardP}>
        Welcome, <strong>{workspace.name}</strong>. Pick a plan to activate the API and start receiving real-time procurement notices.
      </p>
      <div style={{ marginTop: 24 }}>
        <PlanPicker workspaceId={workspace.id} />
      </div>
    </Card>
  );
}

function QuickstartCard({ workspace }) {
  const [keys, setKeys] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [newKey, setNewKey] = useState(null); // {name, plaintext}

  useEffect(() => {
    fetch(`/v1/keys?workspace_id=${encodeURIComponent(workspace.id)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((d) => setKeys(d.keys || []))
      .catch((e) => setErr(`Could not load keys: ${e}`));
  }, [workspace.id]);

  async function generateKey() {
    setErr(null);
    setBusy(true);
    try {
      const name = `key-${new Date().toISOString().slice(0, 10)}-${Math.random().toString(36).slice(2, 6)}`;
      const res = await fetch('/v1/keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ workspace_id: workspace.id, name }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data?.error?.message || `HTTP ${res.status}`);
        return;
      }
      setNewKey({ name: data.name, plaintext: data.plaintext });
      // refresh list
      const list = await fetch(`/v1/keys?workspace_id=${encodeURIComponent(workspace.id)}`, { credentials: 'include' }).then((r) => r.json());
      setKeys(list.keys || []);
    } catch (ex) {
      setErr(ex.message || 'Network error');
    } finally {
      setBusy(false);
    }
  }

  async function revokeKey(id) {
    if (!confirm('Revoke this key? Future API calls using it will be rejected immediately.')) return;
    try {
      const res = await fetch(`/v1/keys/${encodeURIComponent(id)}?workspace_id=${encodeURIComponent(workspace.id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) {
        const d = await res.json().catch(() => null);
        setErr(d?.error?.message || `HTTP ${res.status}`);
        return;
      }
      setKeys(keys.filter((k) => k.id !== id));
    } catch (ex) {
      setErr(ex.message || 'Network error');
    }
  }

  return (
    <Card wide>
      <CardIcon>
        <Sparkles size={28} strokeWidth={2} color="var(--accent)" />
      </CardIcon>
      <h1 style={cardH1}>Welcome to {workspace.name}</h1>
      <p style={cardP}>Workspace active. Generate API keys below  -  your first call lights up the live feed.</p>

      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button onClick={generateKey} disabled={busy} style={primaryBtn}>
          {busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />} Generate API key
        </button>

        {err && <div role="alert" style={{ padding: 10, background: '#fef2f2', border: '1px solid #d33', borderRadius: 6, color: '#d33', fontSize: 13 }}>{err}</div>}

        {keys != null && keys.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Your keys</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {keys.map((k) => (
                <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10, border: '1px solid var(--line-2)', borderRadius: 6, background: 'var(--bg-2)' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{k.name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)' }}>tk_{k.prefix}_•••• {k.last_4}</div>
                  </div>
                  {!k.revoked_at && (
                    <button onClick={() => revokeKey(k.id)} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--danger, #d33)' }} aria-label="Revoke">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p style={{ ...cardP, fontSize: 12, marginTop: 24, color: 'var(--fg-3)' }}>
        Workspace ID: <code style={{ fontFamily: 'var(--font-mono)' }}>{workspace.id}</code>
      </p>

      {newKey && (
        <ApiKeyModal name={newKey.name} plaintext={newKey.plaintext} onClose={() => setNewKey(null)} />
      )}
    </Card>
  );
}

function SuspendedCard({ workspace }) {
  return (
    <Card>
      <CardIcon>
        <AlertTriangle size={28} strokeWidth={2} color="var(--danger)" />
      </CardIcon>
      <h1 style={cardH1}>Workspace suspended</h1>
      <p style={cardP}>
        <strong>{workspace.name}</strong> has been suspended. Update your billing details from the customer portal to reactivate.
      </p>
      <a href="/pricing" style={primaryBtn}>Open billing</a>
    </Card>
  );
}

function ClosedCard() {
  return (
    <Card>
      <CardIcon>
        <AlertTriangle size={28} strokeWidth={2} color="var(--danger)" />
      </CardIcon>
      <h1 style={cardH1}>Workspace closed</h1>
      <p style={cardP}>This workspace was closed. Contact support if you need to reopen it.</p>
    </Card>
  );
}

function SpinnerCard() {
  return (
    <Card>
      <Loader2 size={24} className="spin" />
      <p style={cardP}>Loading your workspace…</p>
    </Card>
  );
}

function ErrorCard({ message }) {
  return (
    <Card>
      <AlertTriangle size={24} color="var(--danger)" />
      <p style={cardP}>{message}</p>
    </Card>
  );
}

function Card({ children, wide }) {
  return (
    <div style={{ ...card, ...(wide ? { maxWidth: 720 } : {}) }}>
      {children}
    </div>
  );
}

function CardIcon({ children }) {
  return (
    <div style={{ marginBottom: 16 }}>{children}</div>
  );
}

const page = { minHeight: '100vh', background: 'var(--bg-0)', color: 'var(--fg-1)' };
const hdr = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '18px 24px',
  borderBottom: '1px solid var(--line-1)',
  background: 'var(--bg-1)',
};
const brand = { fontSize: 16, fontWeight: 700 };
const badge = {
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
  padding: '4px 10px',
  borderRadius: 'var(--r-3)',
  background: 'var(--bg-2)',
  border: '1px solid var(--line-2)',
  color: 'var(--fg-2)',
};
const logoutBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  background: 'var(--bg-1)',
  border: '1px solid var(--line-2)',
  borderRadius: 'var(--r-2)',
  color: 'var(--fg-1)',
  fontSize: 13,
  cursor: 'pointer',
};
const main = { display: 'flex', justifyContent: 'center', padding: '64px 24px' };
const card = {
  maxWidth: 560,
  width: '100%',
  background: 'var(--bg-1)',
  border: '1px solid var(--line-1)',
  borderRadius: 'var(--r-3)',
  padding: 40,
  textAlign: 'left',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};
const cardH1 = { fontSize: 24, fontWeight: 700, margin: '0 0 12px', color: 'var(--fg-0)' };
const cardP = { margin: 0, color: 'var(--fg-2)', lineHeight: 1.55, fontSize: 15 };
const primaryBtn = {
  display: 'inline-block',
  padding: '12px 20px',
  background: 'var(--accent)',
  color: 'var(--accent-fg)',
  borderRadius: 'var(--r-3)',
  fontWeight: 600,
  fontSize: 14,
  textDecoration: 'none',
};
const secondaryBtn = {
  display: 'inline-block',
  padding: '12px 20px',
  background: 'var(--bg-2)',
  color: 'var(--fg-1)',
  border: '1px solid var(--line-2)',
  borderRadius: 'var(--r-3)',
  fontWeight: 600,
  fontSize: 14,
};
